import $ from "../../libs/jquery-3.3.1.min.js"
import {BaseMiniGame} from "./BaseMiniGame";
import * as PIXI from "pixi.js";

// Game description : Sliding outside image to find an object
export class GameB extends BaseMiniGame {

  // Constructor
  constructor(data) {
    super(data);
    // Canvas/Background sizes
    this.appWidth = this.data.appDimensions.width;
    this.appHeight = this.data.appDimensions.height;
    this.backgroundWidth = this.data.backgroundDimensions.width;
    this.backgroundHeight = this.data.backgroundDimensions.height;
    this.appPadding = 50; // empty space that surround the game

    // Initial positions
    this.initialBackgroundPos = {x: 0, y: 0};
    this.initialSliderHandlePos = {x: 0, y: 0};

    // Objective box
    this.objectiveBox = this.data.objectiveBox;
    this.checkObjectiveTimer = false;
    this.checkObjectiveTimerDelay = 1000; // time between drag stop and objective check
  }

  async assetLoader() {
    await super.assetLoader();

    if (this.data.medias && this.data.medias.length) {
      for (let i = 0; i < this.data.medias.length; i++) {
        this.assets[this.data.medias[i].name] = await PIXI.Assets.load(this.data.medias[i].url);
      }
    }
  }

  async buildStage() {
    this.stage.eventMode = 'static';

    // Enable events on the whole stage
    this.stage.hitArea = this.screen;

    this.backgroundSprite = new PIXI.Sprite(this.assets.background);
    this.backgroundSprite.width = this.data.imageDimensions.width;
    this.backgroundSprite.height = this.data.imageDimensions.height;

    this.initialBackgroundPos = {
      x: 0,
      y: -this.backgroundSprite.height / 2 + this.backgroundHeight / 2
    };

    // Prepare moving container
    // Set the moving container the same dimension as the background image
    this.movingContainer = new PIXI.Container();
    this.movingContainer.position = this.initialBackgroundPos;
    this.movingContainer.addChild(this.backgroundSprite);

    // Prepare the objective
    // This is the "box" that needs to be visible in the viewport to complete the challenge
    this.objectiveBoxSprite = new PIXI.Sprite(PIXI.Texture.WHITE);
    this.objectiveBoxSprite.width = this.objectiveBox.width;
    this.objectiveBoxSprite.height = this.objectiveBox.height;
    this.objectiveBoxSprite.position.x = this.objectiveBox.x;
    this.objectiveBoxSprite.position.y = this.objectiveBox.y;
    this.objectiveBoxSprite.alpha = 0.5;
    this.movingContainer.addChild(this.objectiveBoxSprite);

    // Prepare the background container
    this.backgroundContainer = new PIXI.Container();
    this.backgroundContainer.position.x = this.appPadding;
    this.backgroundContainer.position.y = this.appPadding;
    this.backgroundContainer.mask = new PIXI.Graphics().rect(this.appPadding, this.appPadding, this.backgroundWidth, this.backgroundHeight).fill(0xffffff);
    this.backgroundContainer.addChild(this.movingContainer);

    // Prepare the slider container
    this.sliderContainer = new PIXI.Container();
    this.sliderBackgroundContainer = new PIXI.Container();
    this.sliderGraphic = new PIXI.Graphics().rect(0, 0, this.backgroundWidth, 20).fill({color: 0x8b8b8b});
    this.sliderBackgroundContainer.addChild(this.sliderGraphic);

    // Prepare slider background progress
    this.sliderProgressGraphic = new PIXI.Graphics().rect(0, 0, this.backgroundWidth, 20).fill({color: 0xFFFFFF});
    this.sliderProgressGraphic.width = 0;
    this.sliderBackgroundContainer.addChild(this.sliderProgressGraphic);

    // Prepare slider handle
    this.sliderHandleGraphic = new PIXI.Graphics().circle(0, 0, 18).fill({color: 0xffffff});
    this.initialSliderHandlePos = {
      x: this.sliderHandleGraphic.width / 2,
      y: this.sliderGraphic.height / 2
    }
    this.sliderHandleGraphic.position = { ...this.initialSliderHandlePos };
    this.sliderHandleGraphic.eventMode = "static";
    this.sliderHandleGraphic.cursor = "pointer";
    this.sliderHandleGraphic.on("pointerdown", (e) => {
      this.handleOnDragStart(e);
    }).on("pointerup", (e) => {
      this.handleOnDragEnd(e);
    }).on("pointerupoutside", (e) => {
      this.handleOnDragEnd(e);
    });

    this.sliderContainer.position.x = this.appPadding;
    this.sliderContainer.position.y = this.appHeight - this.sliderHandleGraphic.height - this.appPadding;
    this.sliderBackgroundContainer.mask = new PIXI.Graphics().roundRect(this.sliderContainer.position.x, this.sliderContainer.position.y, this.backgroundWidth, 20, 20).fill(0xffffff);
    this.sliderContainer.addChild(this.sliderBackgroundContainer);
    this.sliderContainer.addChild(this.sliderHandleGraphic);


    // Add elements to stage
    this.stage.addChild(this.backgroundContainer);
    this.stage.addChild(this.sliderContainer);
  }

  handleOnDragStart(e) {
    this.stage.eventMode = 'static';
    this.stage.on("pointermove", (e) => {
      this.handleOnDrag(e);
    });
  }

  handleOnDragEnd(e) {
    this.stage.eventMode = 'auto';
    this.stage.off("pointermove");
  }

  handleOnDrag(e) {
    // Update handle position
    const handleHalfWidth = this.sliderHandleGraphic.width / 2;
    this.sliderHandleGraphic.position.x = Math.max(handleHalfWidth, Math.min(this.sliderGraphic.toLocal(e.global).x, this.sliderGraphic.width - handleHalfWidth));
    const handlePositionPerc = window.map(this.sliderHandleGraphic.position.x, handleHalfWidth, this.sliderGraphic.width - handleHalfWidth, 0, 100);

    // Update moving container position
    this.movingContainer.position.x = window.map(handlePositionPerc, 0, 100, 0, -this.backgroundSprite.width + this.backgroundWidth);
    this.sliderProgressGraphic.width = window.map(handlePositionPerc, 0, 100, handleHalfWidth, this.backgroundWidth);

    // Check if objective is found
    this.checkObjectiveFound();
  }

  checkObjectiveFound() {

    // give a little delay when the user stops moving to check if objective is on screen
    if (this.checkObjectiveTimer !== false) {
      clearTimeout(this.checkObjectiveTimer);
      this.checkObjectiveTimer = false;
    }

    this.checkObjectiveTimer = setTimeout(() => {
      const x = this.movingContainer.position.x;
      const y = this.movingContainer.position.y;

      let viewingArea = {
        x1: -x,
        x2: -x + this.backgroundWidth,
        y1: -y,
        y2: -y + this.backgroundHeight,
      }

      if (
        (this.objectiveBoxSprite.x >= viewingArea.x1) &&
        ((this.objectiveBoxSprite.x + this.objectiveBoxSprite.width) <= viewingArea.x2) &&
        (this.objectiveBoxSprite.y >= viewingArea.y1) &&
        ((this.objectiveBoxSprite.y + this.objectiveBoxSprite.height) <= viewingArea.y2)
      ) {
        // Objective is on screen! Complete the challenge
        this.complete();
      }

    }, this.checkObjectiveTimerDelay);
  }

  reset() {
    //console.log("gameB Reset");
    // TODO: There's more things to reset

    // Reset moving container position
    this.movingContainer.position = this.initialBackgroundPos;

    // Reset slider handle position
    this.sliderHandleGraphic.position = this.initialSliderHandlePos;
    this.sliderProgressGraphic.width = 0;
  }
}
